import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import ConfirmDialog from "../../components/ConfirmDialog";
import Item from "../../components/Item";
import KeywordBox from "../../components/KeywordBox";
import CreditService from "./CreditService";
import { usageLogPuzzleTitle } from "./creditUsageDisplay";
import AppCodeSelect from "./AppCodeSelect";

const TAB_APPS = 0;
const TAB_WALLETS = 1;
const TAB_IAP = 2;
const TAB_GUEST = 3;
const TAB_USAGE = 4;
const TAB_PRODUCTS = 5;

/** Test devices on the Guest tab — Android phone and Mac (Word Wheel Quest). */
const TEST_GUEST_DEVICES = [
    { id: "ww-1a0b010dbde-813aadcb", name: "Android phone" },
    { id: "ww-1a0aef2409d-a9e32419", name: "Mac" },
];
const TEST_GUEST_DEVICE_IDS = TEST_GUEST_DEVICES.map((device) => device.id);

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function apiFailureMessage(result, fallback = "Request failed") {
    if (result && typeof result === "object" && result.code === "FAILURE") {
        return result.message || fallback;
    }
    return null;
}

function PagedGrid({
    rows,
    columns,
    rowCount,
    pageModel,
    onPageModelChange,
    sort,
    onSortChange,
    loading,
}) {
    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationModel={pageModel}
            onPaginationModelChange={onPageModelChange}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            sortingMode="server"
            sortModel={sort}
            onSortModelChange={onSortChange}
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            hideFooterSelectedRowCount
            initialState={{ columns: { columnVisibilityModel: { id: true } } }}
        />
    );
}

export default function CreditMain() {
    const [tab, setTab] = useState(TAB_APPS);
    const [loading, setLoading] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "createdAt", sort: "desc" }]);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    const [keyword, setKeyword] = useState("");
    const [filterUserId, setFilterUserId] = useState("");
    const [filterDeviceId, setFilterDeviceId] = useState("");
    const [filterAppCode, setFilterAppCode] = useState("");
    const [filterProductId, setFilterProductId] = useState("");
    const [filterProductGroup, setFilterProductGroup] = useState("");
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [excludeAppleStaff, setExcludeAppleStaff] = useState(true);
    const [excludeTestDevice, setExcludeTestDevice] = useState(true);
    const [testDeviceInfoOpen, setTestDeviceInfoOpen] = useState(false);

    const [products, setProducts] = useState([]);
    const [productGroups, setProductGroups] = useState([]);

    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const [appDialogOpen, setAppDialogOpen] = useState(false);
    const [appDialogMode, setAppDialogMode] = useState("create");
    const [appForm, setAppForm] = useState({ id: "", code: "", name: "" });

    const [adjustOpen, setAdjustOpen] = useState(false);
    const [adjustForm, setAdjustForm] = useState({ userId: "", appCode: "", delta: "" });
    const [moApps, setMoApps] = useState([]);

    const [deleteAppOpen, setDeleteAppOpen] = useState(false);
    const [deleteAppTarget, setDeleteAppTarget] = useState(null);
    const [deletingApp, setDeletingApp] = useState(false);

    const showSnack = useCallback((severity, message) => {
        setSnackbar({ open: true, severity, message });
    }, []);

    const bumpReload = useCallback(() => setReloadToken((t) => t + 1), []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const apps = await CreditService.listAllApps();
                if (!cancelled) setMoApps(apps);
            } catch (e) {
                Logger.error("CreditMain list apps:", e);
                if (!cancelled) setMoApps([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    useEffect(() => {
        setPageModel((prev) => ({ ...prev, page: 0 }));
    }, [tab, keyword, filterUserId, filterDeviceId, filterAppCode, filterProductId, filterProductGroup, excludeStaff, excludeAppleStaff, excludeTestDevice]);

    const displayedProducts = useMemo(() => {
        const groupNeedle = filterProductGroup.trim().toLowerCase();
        if (!groupNeedle) return products;
        return products.filter((row) => row.group.toLowerCase().includes(groupNeedle));
    }, [products, filterProductGroup]);

    const productGroupOptions = useMemo(
        () => [...new Set(products.map((row) => row.group).filter(Boolean))].sort(),
        [products]
    );

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (tab === TAB_PRODUCTS) {
                setLoading(true);
                try {
                    const result = await CreditService.listProducts();
                    if (cancelled) return;
                    const parsed = CreditService.parseProductCatalog(result);
                    setProductGroups(parsed.groups);
                    setProducts(parsed.rows);
                } catch (e) {
                    Logger.error("CreditMain products:", e);
                    if (!cancelled) showSnack("error", "Failed to load product catalog");
                } finally {
                    if (!cancelled) setLoading(false);
                }
                return;
            }

            setLoading(true);
            try {
                let result;
                if (tab === TAB_APPS) {
                    result = await CreditService.listApps(pageModel, sort, keyword);
                } else if (tab === TAB_WALLETS) {
                    result = await CreditService.listWallets(pageModel, sort, {
                        userId: filterUserId.trim(),
                        appCode: filterAppCode.trim(),
                    });
                } else if (tab === TAB_GUEST) {
                    result = await CreditService.listGuestWallets(pageModel, sort, {
                        deviceId: filterDeviceId.trim(),
                        appCode: filterAppCode.trim(),
                        excludeDeviceIds: excludeTestDevice ? TEST_GUEST_DEVICE_IDS : [],
                    });
                } else if (tab === TAB_IAP) {
                    result = await CreditService.listIap(pageModel, sort, {
                        userId: filterUserId.trim(),
                        appCode: filterAppCode.trim(),
                        productId: filterProductId.trim(),
                        excludeStaff,
                        excludeAppleStaff,
                    });
                } else {
                    result = await CreditService.listUsage(pageModel, sort, {
                        userId: filterUserId.trim(),
                        appCode: filterAppCode.trim(),
                    });
                }
                if (cancelled) return;
                setRows(
                    (result?.content || []).map((row) => ({
                        ...row,
                        id: row.id,
                    }))
                );
                setRowCount(CreditService.totalElements(result));
            } catch (e) {
                Logger.error("CreditMain list:", e);
                if (!cancelled) {
                    setRows([]);
                    setRowCount(0);
                    showSnack("error", "Failed to load credit data");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [tab, pageModel, sort, keyword, filterUserId, filterDeviceId, filterAppCode, filterProductId, excludeStaff, excludeAppleStaff, excludeTestDevice, reloadToken, showSnack]);

    const handleTabChange = (_e, next) => {
        setTab(next);
        if (next === TAB_APPS) setSort([{ field: "createdAt", sort: "desc" }]);
        else if (next === TAB_WALLETS || next === TAB_GUEST) setSort([{ field: "updatedAt", sort: "desc" }]);
        else if (next === TAB_IAP) setSort([{ field: "purchaseDate", sort: "desc" }]);
        else if (next === TAB_USAGE) setSort([{ field: "usedAt", sort: "desc" }]);
    };

    const openCreateApp = () => {
        setAppDialogMode("create");
        setAppForm({ id: "", code: "", name: "" });
        setAppDialogOpen(true);
    };

    const openEditApp = (row) => {
        setAppDialogMode("edit");
        setAppForm({ id: row.id, code: row.code ?? "", name: row.name ?? "" });
        setAppDialogOpen(true);
    };

    const handleSaveApp = async () => {
        const code = appForm.code.trim();
        const name = appForm.name.trim();
        if (!code || !name) {
            showSnack("warning", "Code and name are required");
            return;
        }
        try {
            const result =
                appDialogMode === "create"
                    ? await CreditService.createApp({ code, name })
                    : await CreditService.updateApp(appForm.id, { code, name });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            showSnack("success", appDialogMode === "create" ? "App created" : "App updated");
            setAppDialogOpen(false);
            bumpReload();
        } catch (e) {
            Logger.error("CreditMain save app:", e);
            showSnack("error", "Failed to save app");
        }
    };

    const handleAdjustWallet = async () => {
        const userId = adjustForm.userId.trim();
        const appCode = adjustForm.appCode.trim();
        const delta = Number(adjustForm.delta);
        if (!userId || !appCode || !Number.isFinite(delta) || delta === 0) {
            showSnack("warning", "User id, app code, and non-zero delta are required");
            return;
        }
        try {
            const result = await CreditService.adjustWallet({ userId, appCode, delta });
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            showSnack("success", `Balance updated (${delta > 0 ? "+" : ""}${delta})`);
            setAdjustOpen(false);
            setAdjustForm({ userId: "", appCode: "", delta: "" });
            bumpReload();
        } catch (e) {
            Logger.error("CreditMain adjust wallet:", e);
            showSnack("error", "Failed to adjust wallet");
        }
    };

    const openDeleteApp = (row) => {
        setDeleteAppTarget(row);
        setDeleteAppOpen(true);
    };

    const handleDeleteApp = async () => {
        const id = deleteAppTarget?.id;
        if (!id) return;
        setDeletingApp(true);
        try {
            const result = await CreditService.deleteApp(id);
            const err = apiFailureMessage(result);
            if (err) {
                showSnack("error", err);
                return;
            }
            showSnack("success", `Deleted app "${deleteAppTarget?.code ?? id}"`);
            setDeleteAppOpen(false);
            setDeleteAppTarget(null);
            bumpReload();
        } catch (e) {
            Logger.error("CreditMain delete app:", e);
            showSnack("error", e?.message || "Failed to delete app");
        } finally {
            setDeletingApp(false);
        }
    };

    const appColumns = useMemo(
        () => [
            { field: "code", headerName: "Code", flex: 1, minWidth: 160 },
            { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
            {
                field: "createdAt",
                headerName: "Created",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
            {
                field: "actions",
                headerName: "",
                width: 96,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Tooltip title="Edit app">
                            <IconButton size="small" aria-label="Edit app" onClick={() => openEditApp(params.row)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete app">
                            <IconButton
                                size="small"
                                aria-label="Delete app"
                                color="error"
                                onClick={() => openDeleteApp(params.row)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
            },
        ],
        []
    );

    const walletColumns = useMemo(
        () => [
            { field: "userId", headerName: "User ID", flex: 1, minWidth: 220 },
            { field: "appCode", headerName: "App code", width: 180 },
            { field: "appName", headerName: "App name", width: 160 },
            { field: "creditBalance", headerName: "Balance", width: 100, type: "number" },
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        []
    );

    const guestColumns = useMemo(
        () => [
            { field: "deviceId", headerName: "Device ID", flex: 1, minWidth: 220 },
            { field: "appCode", headerName: "App code", width: 180 },
            { field: "appName", headerName: "App name", width: 160 },
            { field: "creditBalance", headerName: "Balance", width: 100, type: "number" },
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        []
    );

    const iapColumns = useMemo(
        () => [
            { field: "transactionId", headerName: "Transaction ID", flex: 1, minWidth: 200 },
            { field: "email", headerName: "Email", width: 220 },
            { field: "appCode", headerName: "App", width: 160 },
            { field: "productId", headerName: "Product", width: 200 },
            { field: "creditsAwarded", headerName: "Credits", width: 90, type: "number" },
            {
                field: "purchaseDate",
                headerName: "Purchased",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        []
    );

    const usageColumns = useMemo(
        () => [
            { field: "userId", headerName: "User ID", width: 200 },
            { field: "appCode", headerName: "App", width: 160 },
            {
                field: "puzzleTitle",
                headerName: "Puzzle title",
                flex: 1,
                minWidth: 180,
                valueGetter: (_value, row) => usageLogPuzzleTitle(row),
            },
            { field: "featureUsed", headerName: "Feature", width: 160 },
            { field: "creditsConsumed", headerName: "Credits", width: 90, type: "number" },
            {
                field: "usedAt",
                headerName: "Used at",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        []
    );

    const productColumns = useMemo(
        () => [
            { field: "group", headerName: "Config group", width: 220 },
            { field: "productId", headerName: "Product ID", flex: 1, minWidth: 260 },
            { field: "creditsAwarded", headerName: "Credits awarded", width: 140, type: "number" },
        ],
        []
    );

    const showWalletFilters = tab === TAB_WALLETS || tab === TAB_IAP || tab === TAB_USAGE;
    const showGuestFilters = tab === TAB_GUEST;
    const showProductFilter = tab === TAB_IAP;
    const showProductCatalogFilters = tab === TAB_PRODUCTS;

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Mobile Credit
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Manage mobile app wallets, IAP audit logs, and usage for native games (API:{" "}
                            <code>/v1/credit</code>). Product catalog is read-only from{" "}
                            <code>application.yml</code> (<code>app.mobile-credit.apps.*</code>).
                        </Typography>
                        {tab === TAB_GUEST ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Guest wallets from <code>mo_guest_credits</code>. These users watched an ad and
                                received credits (unsigned devices, keyed by device ID).
                            </Typography>
                        ) : null}
                        {tab === TAB_PRODUCTS && productGroups.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                {productGroups.length} config group{productGroups.length === 1 ? "" : "s"},{" "}
                                {products.length} product{products.length === 1 ? "" : "s"}
                            </Typography>
                        ) : null}
                        <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                            <Tab label="Apps" />
                            <Tab label="Wallets" />
                            <Tab label="IAP transactions" />
                            <Tab label="Guest" />
                            <Tab label="Usage log" />
                            <Tab label="Product catalog" />
                        </Tabs>
                    </Item>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Item
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", flex: 1 }}>
                            {tab === TAB_APPS ? <KeywordBox callback={setKeyword} /> : null}
                            {showWalletFilters ? (
                                <>
                                    <TextField
                                        size="small"
                                        label="User ID"
                                        value={filterUserId}
                                        onChange={(e) => setFilterUserId(e.target.value)}
                                        sx={{ minWidth: 220 }}
                                    />
                                    <AppCodeSelect
                                        apps={moApps}
                                        value={filterAppCode}
                                        onChange={setFilterAppCode}
                                        label="App code"
                                        allowAll
                                        fullWidth={false}
                                        size="small"
                                        sx={{ minWidth: 280 }}
                                    />
                                </>
                            ) : null}
                            {showGuestFilters ? (
                                <>
                                    <TextField
                                        size="small"
                                        label="Device ID"
                                        value={filterDeviceId}
                                        onChange={(e) => setFilterDeviceId(e.target.value)}
                                        sx={{ minWidth: 220 }}
                                    />
                                    <AppCodeSelect
                                        apps={moApps}
                                        value={filterAppCode}
                                        onChange={setFilterAppCode}
                                        label="App code"
                                        allowAll
                                        fullWidth={false}
                                        size="small"
                                        sx={{ minWidth: 280 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={excludeTestDevice}
                                                onChange={(e) => setExcludeTestDevice(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Exclude test device"
                                        sx={{ ml: 0, minHeight: 44 }}
                                    />
                                    <Tooltip title="Test devices">
                                        <IconButton
                                            size="small"
                                            aria-label="Test device IDs and names"
                                            onClick={() => setTestDeviceInfoOpen(true)}
                                            sx={{ minWidth: 44, minHeight: 44 }}
                                        >
                                            <InfoOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : null}
                            {showProductCatalogFilters ? (
                                <TextField
                                    size="small"
                                    label="Config group"
                                    value={filterProductGroup}
                                    onChange={(e) => setFilterProductGroup(e.target.value)}
                                    placeholder="appleSlidingPuzzleQuest"
                                    helperText={
                                        productGroupOptions.length
                                            ? productGroupOptions.join(", ")
                                            : undefined
                                    }
                                    slotProps={{
                                        formHelperText: { sx: { maxWidth: 480 } },
                                    }}
                                    sx={{ minWidth: 260 }}
                                />
                            ) : null}
                            {showProductFilter ? (
                                <>
                                    <TextField
                                        size="small"
                                        label="Product ID"
                                        value={filterProductId}
                                        onChange={(e) => setFilterProductId(e.target.value)}
                                        sx={{ minWidth: 240 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={excludeStaff}
                                                onChange={(e) => setExcludeStaff(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Exclude staff"
                                        sx={{ ml: 0, minHeight: 44 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={excludeAppleStaff}
                                                onChange={(e) => setExcludeAppleStaff(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Exclude Apple staff"
                                        sx={{ ml: 0, minHeight: 44 }}
                                    />
                                </>
                            ) : null}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {tab === TAB_APPS ? (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateApp}>
                                    New app
                                </Button>
                            ) : null}
                            {tab === TAB_WALLETS ? (
                                <Button variant="contained" startIcon={<TuneIcon />} onClick={() => setAdjustOpen(true)}>
                                    Adjust balance
                                </Button>
                            ) : null}
                        </Box>
                    </Item>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Item>
                        {tab === TAB_PRODUCTS ? (
                            <DataGrid
                                rows={displayedProducts}
                                columns={productColumns}
                                loading={loading}
                                disableRowSelectionOnClick
                                autoHeight
                                hideFooter={displayedProducts.length <= 25}
                                pageSizeOptions={[25, 50]}
                                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            />
                        ) : (
                            <PagedGrid
                                rows={rows}
                                columns={
                                    tab === TAB_APPS
                                        ? appColumns
                                        : tab === TAB_WALLETS
                                          ? walletColumns
                                          : tab === TAB_GUEST
                                            ? guestColumns
                                            : tab === TAB_IAP
                                              ? iapColumns
                                              : usageColumns
                                }
                                rowCount={rowCount}
                                pageModel={pageModel}
                                onPageModelChange={setPageModel}
                                sort={sort}
                                onSortChange={setSort}
                                loading={loading}
                            />
                        )}
                    </Item>
                </Grid>
            </Grid>

            <Dialog open={appDialogOpen} onClose={() => setAppDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{appDialogMode === "create" ? "New mobile app" : "Edit mobile app"}</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <TextField
                        label="Code"
                        value={appForm.code}
                        onChange={(e) => setAppForm((f) => ({ ...f, code: e.target.value }))}
                        placeholder="sliding_puzzle_quest"
                        fullWidth
                        autoFocus
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <TextField
                        label="Name"
                        value={appForm.name}
                        onChange={(e) => setAppForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Sliding Puzzle Quest"
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAppDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveApp}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adjust wallet balance</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 3,
                        overflow: "visible",
                    }}
                >
                    <TextField
                        label="User ID"
                        value={adjustForm.userId}
                        onChange={(e) => setAdjustForm((f) => ({ ...f, userId: e.target.value }))}
                        fullWidth
                        autoFocus
                    />
                    <AppCodeSelect
                        apps={moApps}
                        value={adjustForm.appCode}
                        onChange={(appCode) => setAdjustForm((f) => ({ ...f, appCode }))}
                        label="App"
                    />
                    <TextField
                        label="Delta"
                        type="number"
                        value={adjustForm.delta}
                        onChange={(e) => setAdjustForm((f) => ({ ...f, delta: e.target.value }))}
                        helperText="Positive to grant credits; negative to deduct (balance cannot go below 0). e.g. +3 or -2"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdjustOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAdjustWallet}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={testDeviceInfoOpen} onClose={() => setTestDeviceInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Test devices</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {TEST_GUEST_DEVICES.map((device) => (
                        <Box key={device.id} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">{device.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                {device.id}
                            </Typography>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDeviceInfoOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={deleteAppOpen}
                setOpen={(open) => {
                    if (!deletingApp) {
                        setDeleteAppOpen(open);
                        if (!open) setDeleteAppTarget(null);
                    }
                }}
                title="Delete mobile app?"
                message="This removes the app code from mo_apps. Wallets and audit rows for this app may remain in the database."
                targetName={deleteAppTarget?.code ?? ""}
                warningAlert={
                    deleteAppTarget?.name
                        ? `App name: ${deleteAppTarget.name}`
                        : "This action cannot be undone from the admin UI."
                }
                callback={handleDeleteApp}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
